import type { TerrainSampler } from "../world/types";
import { slopeSupport } from "./slope";

/*
 * Axis-aligned box against voxel terrain: move, then resolve, one axis at a time.
 *
 * Host-side physics, per plan decision 7 — the player's transform lives in the
 * client for Phase 1 and collides against terrain queries the sim answers. This
 * is float arithmetic and that is fine: none of it feeds the simulation, so none
 * of it can desync anything (rust guide §9.2 draws the line at derived,
 * renderer-facing values, and a camera on legs is as derived as it gets).
 *
 * The technique is the standard one for voxel worlds and its correctness rests
 * on one property: **each axis is moved and resolved separately**. Resolving all
 * three at once needs the collision *time* along the path to know which face was
 * hit; resolving them in sequence needs only "does the box overlap a solid tile
 * now", and sliding along walls falls out for free.
 *
 * Two shapes do not fit in that sentence, and they are the reason for the extra
 * resolution below: a ramp and a flight of stairs are walkable and **not
 * solid**, so `isSolid` cannot see the surface the mesher draws over either of
 * them (`player/slope.ts`). Rising ground is resolved against a *height* rather
 * than against a face, and it is done last, because where the slope holds the
 * box up depends on where the box finished moving.
 *
 * **Nothing in this file names a shape** — that is `player/slope.ts`'s whole
 * job, and it is why chunk St put the player on a town's steps without touching
 * a line of the sweep: DF's table gives a `STAIR_UP` the same row it gives a
 * `RAMP` (map §5.3), so every sentence below was already true of a flight the
 * moment the height query answered for one.
 */

/** A tile occupies the half-open cube `[x, x+1) x [y, y+1) x [z, z+1)`. */
export type Axis = "x" | "y" | "z";

/** An axis-aligned box in world tile units. */
export interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

/** What one {@link moveAabb} call ran into. */
export interface MoveOutcome {
  /** True when the box was stopped moving along that axis. */
  readonly blockedX: boolean;
  readonly blockedY: boolean;
  readonly blockedZ: boolean;

  /** True when the box was stopped while moving downwards — i.e. it is standing on something. */
  readonly landed: boolean;

  /** True when the box was stopped while moving upwards — i.e. it hit a ceiling. */
  readonly hitCeiling: boolean;
}

/**
 * The gap left between a resolved box and the surface it was stopped by.
 *
 * Snapping exactly flush puts the box's face on a tile boundary, where the next
 * frame's floor() can land either side of it and the box alternates between
 * penetrating and not. A tenth of a millimetre of daylight is invisible and
 * removes the ambiguity.
 */
const SKIN = 1e-4;

/**
 * The largest distance moved in one resolution pass.
 *
 * Move-then-resolve cannot see a tile it jumped clean over, so a step longer
 * than a tile could tunnel. Splitting long moves into sub-steps costs nothing in
 * the common case (a walking player moves ~0.15 tiles per tick at 30 Hz) and
 * makes terminal-velocity falls and any future speed boost safe by construction.
 */
const MAX_SUBSTEP = 0.5;

/**
 * How far the box is pulled back down onto a slope it is walking off the top of.
 *
 * Rising ground falls one tile for every tile across — a flight as much as a
 * ramp, since a flight is walked as the line through its treads
 * (`player/slope.ts`) — so a walker coming down one loses at most their own step
 * per tick: 0.15 tiles at 4.5 tiles a second and 30 Hz.
 * Gravity alone starts slower than that, so without the snap the walk down a
 * hillside is a run of small hops: fall behind the slope, catch up, land,
 * repeat. The number is that step with a margin and deliberately no more,
 * because anything much larger would start hauling a player down onto a ramp
 * they had jumped over.
 */
const SLOPE_STICK = 0.25;

/**
 * The tallest slope a body steps up onto sideways, in tiles.
 *
 * Walking *up* rising ground is never near this: a 45-degree slope gives back
 * exactly what the walker puts in, 0.15 tiles a tick, and a flight is walked as
 * the same 45 degrees (`player/slope.ts`). What is near it is walking into the
 * **side** of a run — the end of one, where the mesher closes a ramp with a
 * triangular cap and a flight with a stack of rectangular ones
 * (`crates/mesher/src/ramp.rs`, `crates/mesher/src/steps.rs`). That cap is a
 * face a player can see, so meeting it should feel like meeting a face: low down
 * it is a kerb to step over, further up it is a wall to walk around or jump.
 * Without the limit the same tile is a lift that hoists a walker most of a tile
 * in a single tick, which is four times the speed of a jump and reads as a
 * glitch.
 *
 * **It is also what settled how a flight is walked.** A surface quantised to the
 * mesher's treads has a half-tile riser inside every stair tile, which is twice
 * this — so the climb stalls at the riser and the flight cannot be walked at
 * all, and the only way through is to let a ramp's cap hoist half a tile too.
 * Measured, and recorded in `docs/decisions/witness.md` under chunk St.
 */
const MAX_SLOPE_STEP = 0.25;

/**
 * Moves `box` by the given delta, resolving collisions against solid tiles.
 *
 * Mutates `box` in place — the caller owns exactly one of these per body and a
 * fresh object per tick would be garbage for no benefit.
 */
export function moveAabb(
  box: Aabb,
  dx: number,
  dy: number,
  dz: number,
  terrain: TerrainSampler,
): MoveOutcome {
  const substeps = Math.max(
    1,
    Math.ceil(Math.max(Math.abs(dx), Math.abs(dy), Math.abs(dz)) / MAX_SUBSTEP),
  );

  let blockedX = false;
  let blockedY = false;
  let blockedZ = false;
  let landed = false;
  let hitCeiling = false;

  for (let i = 0; i < substeps; i += 1) {
    // Vertical first, then horizontal. Landing before moving sideways means the
    // player who walks off a ledge and back on lands on the tile they are over
    // at the end of the tick, not the one they started on.
    let onSolid = false;

    if (sweep(box, "z", dz / substeps, terrain)) {
      blockedZ = true;
      landed ||= dz < 0;
      hitCeiling ||= dz > 0;
      onSolid = dz < 0;
    }

    // Settled onto the ramp under it *before* walking as well as after, which
    // reads like belt and braces and is neither. Gravity dips the box a
    // hundredth of a tile every tick before the sweep catches it, and at the top
    // of a ramp that dip is the difference between standing level with the
    // terrace and standing a hair inside the tile the wall occupies — where the
    // horizontal sweep finds the wall and refuses to move. The dip is invisible;
    // being unable to walk off the top of a hill because of it is not.
    let onSlope = settleOnSlope(box, dz, onSolid, terrain);

    blockedX = stride(box, "x", dx / substeps, terrain) || blockedX;
    blockedY = stride(box, "y", dy / substeps, terrain) || blockedY;

    // And again after, because the slope holds the box up wherever it *finished*
    // moving. Per substep rather than per call, so a long fall resolves against
    // every ramp it passes rather than only the one under its landing spot.
    onSlope = settleOnSlope(box, dz, onSolid, terrain) || onSlope;

    if (onSlope) {
      blockedZ = true;
      landed = true;
    }
  }

  return { blockedX, blockedY, blockedZ, landed, hitCeiling };
}

/** Returns `true` when any tile the box overlaps is solid. */
export function overlapsSolid(box: Aabb, terrain: TerrainSampler): boolean {
  return findBlocker(box, "x", true, terrain) !== null;
}

/**
 * Moves the box along one horizontal axis, against solid tiles *and* against the
 * slopes it would have to climb.
 *
 * The sweep by itself answers "is a cube in the way"; a ramp is not a cube, so
 * the second half asks "how far up would this leave me". A step that would hoist
 * the box more than {@link MAX_SLOPE_STEP} is refused whole, and refusing it
 * one axis at a time is what keeps a body sliding along the end of a ramp run
 * rather than sticking to it — the same reason the three sweeps are separate.
 */
function stride(box: Aabb, axis: Axis, delta: number, terrain: TerrainSampler): boolean {
  if (delta === 0) {
    // Nothing moved, so nothing can have been stepped onto — and the terrain
    // query behind the slope check is a call across the wasm boundary per tile
    // of the footprint, which a standing player should not be paying twice a
    // tick for.
    return false;
  }

  const before = minOf(box, axis);
  const blocked = sweep(box, axis, delta, terrain);
  const support = slopeSupport(box, terrain);

  if (support !== null && support + SKIN - box.minZ > MAX_SLOPE_STEP) {
    translate(box, axis, before - minOf(box, axis));

    return true;
  }

  return blocked;
}

/**
 * Puts the box on the ramp under it, if a ramp is what is under it.
 *
 * Returns `true` when the box was moved, which is a landing in both senses the
 * caller has: the vertical speed is spent and the player is standing on
 * something. `null` support — no ramp anywhere under the footprint — is the case
 * that leaves everything to the sweeps above, and it is almost every call.
 *
 * The two halves are gated differently on purpose:
 *
 * - **Coming up out of the slope is unconditional.** A body inside terrain is a
 *   bug whatever it was doing, and a rising tile is terrain from its floor up to
 *   the surface — DF's table says neither a `RAMP` nor a `STAIR_UP` is
 *   `passable_low` (map §5.3), so nothing falls through one.
 * - **Following the slope down happens only while walking down it.** A body that
 *   is climbing must not be hauled back to the ground it just left, and a body
 *   the sweep has already stood on solid rock is standing on solid rock: the
 *   wall beside a ramp is exactly a tile tall, and the two must not fight over
 *   the tick a player crosses between them.
 */
function settleOnSlope(
  box: Aabb,
  dz: number,
  onSolid: boolean,
  terrain: TerrainSampler,
): boolean {
  const support = slopeSupport(box, terrain);

  if (support === null) {
    return false;
  }

  // The same daylight the sweeps leave, and load-bearing in exactly one place:
  // at the very top of a ramp the surface *is* the boundary between the tile and
  // the one above, and a box resting flush on it is still, to `floor()`, in the
  // tile below — sharing it with the wall the ramp leans on.
  const feet = support + SKIN;
  const drop = box.minZ - feet;

  if (drop < 0 || (dz <= 0 && !onSolid && drop <= SLOPE_STICK)) {
    translate(box, "z", -drop);

    return true;
  }

  return false;
}

/**
 * Moves the box along one axis and snaps it back out of whatever it hit.
 *
 * Returns `true` if it hit something.
 */
function sweep(box: Aabb, axis: Axis, delta: number, terrain: TerrainSampler): boolean {
  if (delta === 0) {
    return false;
  }

  translate(box, axis, delta);

  const forwards = delta > 0;
  const blocker = findBlocker(box, axis, forwards, terrain);

  if (blocker === null) {
    return false;
  }

  // `blocker` is the tile coordinate of the offending tile nearest to where the
  // box came from, so its facing plane is at `blocker` going forwards and
  // `blocker + 1` coming back.
  const correction = forwards
    ? blocker - SKIN - maxOf(box, axis)
    : blocker + 1 + SKIN - minOf(box, axis);
  translate(box, axis, correction);

  return true;
}

/**
 * Returns the coordinate, along `axis`, of the solid tile the box overlaps that
 * is furthest back along the direction of travel — or `null` if it overlaps none.
 *
 * The extreme tile rather than the first one found: with the box already moved,
 * the tile to snap out of is the one whose face the box crossed first, and
 * scanning order must not decide that.
 */
function findBlocker(
  box: Aabb,
  axis: Axis,
  forwards: boolean,
  terrain: TerrainSampler,
): number | null {
  // Half-open tiles: a box whose max lands exactly on a boundary touches the
  // next tile without overlapping it, which `ceil(max) - 1` expresses and
  // `floor(max)` does not.
  const minTileX = Math.floor(box.minX);
  const maxTileX = Math.ceil(box.maxX) - 1;
  const minTileY = Math.floor(box.minY);
  const maxTileY = Math.ceil(box.maxY) - 1;
  const minTileZ = Math.floor(box.minZ);
  const maxTileZ = Math.ceil(box.maxZ) - 1;

  let blocker: number | null = null;

  for (let x = minTileX; x <= maxTileX; x += 1) {
    for (let y = minTileY; y <= maxTileY; y += 1) {
      for (let z = minTileZ; z <= maxTileZ; z += 1) {
        if (!terrain.isSolid(x, y, z)) {
          continue;
        }

        const along = axis === "x" ? x : axis === "y" ? y : z;

        if (blocker === null || (forwards ? along < blocker : along > blocker)) {
          blocker = along;
        }
      }
    }
  }

  return blocker;
}

/** Shifts the whole box along one axis. */
function translate(box: Aabb, axis: Axis, delta: number): void {
  switch (axis) {
    case "x":
      box.minX += delta;
      box.maxX += delta;
      break;
    case "y":
      box.minY += delta;
      box.maxY += delta;
      break;
    case "z":
      box.minZ += delta;
      box.maxZ += delta;
      break;
  }
}

function minOf(box: Aabb, axis: Axis): number {
  switch (axis) {
    case "x":
      return box.minX;
    case "y":
      return box.minY;
    case "z":
      return box.minZ;
  }
}

function maxOf(box: Aabb, axis: Axis): number {
  switch (axis) {
    case "x":
      return box.maxX;
    case "y":
      return box.maxY;
    case "z":
      return box.maxZ;
  }
}
